import {
  $,
  isClickOutside,
  off,
  on,
  setAttr,
  toggleClass,
} from "../../utils/dom";
import { DispatchFunction } from "../../utils/plugin";
import { IDropDownModel } from "./";
import { toggleDropDown } from "./reducer";

type DropDownView = (
  dispatch: DispatchFunction,
  model: IDropDownModel
) => () => void;

interface IViewModel extends IDropDownModel {
  buttonEl: HTMLElement;
}

type ViewComponent = (model: IViewModel) => void;
type ViewDispatchComponent = (
  dispatch: DispatchFunction,
  model: IViewModel
) => () => void;

// Component that adds events and returns cleanup function for those
const events: ViewDispatchComponent = (
  dispatch,
  { el, buttonEl, isClosed }
) => {
  const onButtonClick = (event: Event) => {
    event.preventDefault();
    dispatch(toggleDropDown());
  };
  // when the dropdown is open we want to close it on clicks outside
  // of the main element
  const onDocumentClick = (event: Event) => {
    if (isClickOutside(el, event)) {
      dispatch(toggleDropDown());
    }
  };

  on(buttonEl, "click", onButtonClick);
  if (!isClosed) {
    on(document, "click", onDocumentClick);
  }

  return () => {
    off(buttonEl, "click", onButtonClick);
    off(document, "click", onDocumentClick);
  };
};

// Component used to update the DOM according to state
const ui: ViewComponent = ({
  el,
  closedClass,
  isClosed,
  openButtonClass,
  closedButtonClass,
  buttonEl,
}) => {
  toggleClass(el, closedClass, isClosed);

  // if we want a class to change on the button depending on if dropDown is visible or not
  if (openButtonClass) {
    toggleClass(buttonEl, openButtonClass, !isClosed);
  }
  if (closedButtonClass) {
    toggleClass(buttonEl, closedButtonClass, isClosed);
  }
};

const accessability: ViewComponent = ({ buttonEl, isClosed, contentId }) => {
  const expanded = !isClosed;
  setAttr(buttonEl, "aria-expanded", expanded.toString());

  if (contentId) {
    setAttr(buttonEl, "aria-controls", contentId);
  }
};

const dropDownView: DropDownView = (dispatch, model) => {
  const buttonEl = $(model.buttonSelector, model.el);

  if (model.el && buttonEl) {
    // create our ViewModel
    const viewModel: IViewModel = { ...model, buttonEl };

    // add our events and recieve cleanup function for those
    const cleanupEvents = events(dispatch, viewModel);

    // update the dom according to our state/model
    ui(viewModel);

    // update the accessability attributes
    accessability(viewModel);

    // return our cleanup function, that removes our event listeners
    return cleanupEvents;
  } else {
    // Warn the user if we cannot find the button or the content within our DropDown
    // and fail silently
    if (!buttonEl) {
      // tslint:disable no-console
      console.warn(
        `DropDown: Could not find "button element" with selector ${model.buttonSelector} in ${model.el}`
      );
      // tslint:enable no-console
    }

    return () => ({});
  }
};

export default dropDownView;
